import { useEffect, useRef } from "react";
import { X } from "lucide-react";

/**
 * Shared shell for the two DUO confirmation modals.
 */
export type DuoConfirmModalProps = {
  open: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
};

export function DuoConfirmModal({
  open,
  title,
  body,
  confirmLabel = "Bevestigen",
  cancelLabel = "Annuleren",
  onCancel,
  onConfirm,
}: DuoConfirmModalProps) {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  function handleBackdropClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === ref.current) onCancel();
  }

  function handleCancel(e: React.SyntheticEvent<HTMLDialogElement>) {
    e.preventDefault();
    onCancel();
  }

  return (
    <dialog
      ref={ref}
      data-skin="duo"
      onClick={handleBackdropClick}
      onCancel={handleCancel}
      aria-labelledby="duo-confirm-title"
      aria-describedby="duo-confirm-body"
      className="m-auto w-[480px] max-w-[calc(100%-2rem)] rounded-lg border-0 bg-[var(--duo-card)] p-0 text-[var(--duo-text)] shadow-[0_20px_25px_-5px_rgba(0,0,0,0.10)] backdrop:bg-black/50"
    >
      <div className="px-8 py-6">
        <div className="flex items-center justify-between">
          <h2 id="duo-confirm-title" className="text-[18px] font-semibold">
            {title}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Sluiten"
            className="inline-flex h-8 w-8 items-center justify-center rounded-sm text-[var(--duo-text)] transition-colors hover:bg-[var(--duo-grey-cell)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue)] focus-visible:outline-offset-2"
          >
            <X size={20} strokeWidth={1.8} aria-hidden="true" />
          </button>
        </div>
        <p
          id="duo-confirm-body"
          className="mt-3 text-[14px] leading-relaxed text-[var(--duo-text)]"
        >
          {body}
        </p>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-10 items-center rounded-sm bg-[var(--duo-grey-cell)] px-5 text-[13px] font-bold uppercase tracking-[0.04em] text-[var(--duo-grey-cell-text)] transition-colors hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue)] focus-visible:outline-offset-2"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            autoFocus
            className="inline-flex h-10 items-center rounded-sm bg-[var(--duo-orange)] px-5 text-[13px] font-bold uppercase tracking-[0.04em] text-[var(--duo-orange-text)] transition-colors hover:bg-[var(--duo-orange-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue)] focus-visible:outline-offset-2"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
